import { useCallback, useEffect, useState } from 'react'
import {
  ArrowRight,
  CalendarDays,
  Check,
  Clock3,
  LoaderCircle,
} from 'lucide-react'
import { api } from '../lib/api.js'

function formatDay(day) {
  return new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(new Date(`${day}T12:00:00.000Z`))
}

function formatDuration(milliseconds) {
  if (!Number.isFinite(milliseconds)) return null
  const seconds = Math.max(0, Math.floor(milliseconds / 1_000))
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

export function ArchivesPage({ navigate, nickname }) {
  const [state, setState] = useState({
    loading: true,
    days: [],
    error: '',
  })
  const [loadingMore, setLoadingMore] = useState(false)

  const fetchDays = useCallback(async (options) => {
    try {
      return await api.archiveDays(options)
    } catch (error) {
      if (error.status !== 401 || !nickname) throw error
      await api.saveProfile(nickname)
      return api.archiveDays(options)
    }
  }, [nickname])

  const load = useCallback(async () => {
    const payload = await fetchDays()
    setState({ loading: false, days: payload?.archives || payload?.days || [], error: '' })
  }, [fetchDays])

  useEffect(() => {
    let active = true
    load().catch((error) => {
      if (active) setState((current) => ({ ...current, loading: false, error: error.message }))
    })
    return () => { active = false }
  }, [load])

  const loadMore = async () => {
    const before = state.days.at(-1)?.day
    if (!before) return
    setLoadingMore(true)
    try {
      const payload = await fetchDays({ before })
      const more = payload?.archives || payload?.days || []
      setState((current) => ({
        ...current,
        days: [...current.days, ...more.filter((entry) => !current.days.some((item) => item.day === entry.day))],
      }))
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }))
    } finally {
      setLoadingMore(false)
    }
  }

  if (state.loading) {
    return <main className="game-loading"><LoaderCircle className="spin" size={28} /><p>Ouverture des archives…</p></main>
  }

  return (
    <main className="page section-shell archives-page">
      <div className="page-heading page-heading--row archives-heading">
        <div>
          <p className="eyebrow">Archives quotidiennes</p>
          <h1>Les grilles précédentes.</h1>
          <p>Rejouez gratuitement les défis passés à votre rythme. Ces parties restent hors du classement quotidien.</p>
        </div>
        <span className="archives-emblem"><CalendarDays size={34} /></span>
      </div>

      {state.error ? (
        <section className="archives-gate"><p className="field-error" role="alert">{state.error}</p><button className="button button--ink" type="button" onClick={load}>Réessayer</button></section>
      ) : state.days.length ? (
        <>
          <ol className="archive-list">
            {state.days.map((entry) => {
              const completed = Boolean(entry.play?.completedAt)
              const elapsed = formatDuration(entry.play?.elapsedMs)
              return (
                <li key={entry.day}>
                  <div className="archive-date">
                    <time dateTime={entry.day}>{formatDay(entry.day)}</time>
                    <span>Grille moyenne · 8 × 8</span>
                  </div>
                  <div className="archive-state">
                    {completed ? <span className="archive-complete"><Check size={15} /> Terminée{elapsed ? ` · ${elapsed}` : ''}</span> : entry.play ? <span><Clock3 size={15} /> En cours</span> : <span>À jouer</span>}
                    <button className="button button--ghost" type="button" onClick={() => navigate(`/jouer/archive?jour=${encodeURIComponent(entry.day)}`)}>
                      {completed ? 'Revoir' : entry.play ? 'Continuer' : 'Jouer'} <ArrowRight size={16} />
                    </button>
                  </div>
                </li>
              )
            })}
          </ol>
          {state.days.length >= 31 && <button className="button button--ghost archive-more" type="button" onClick={loadMore} disabled={loadingMore}>{loadingMore ? 'Chargement…' : 'Afficher plus'}</button>}
        </>
      ) : (
        <section className="archives-gate">
          <CalendarDays size={31} />
          <h2>Les archives se remplissent</h2>
          <p>Les défis déjà publiés apparaîtront ici au fil des jours.</p>
          <button className="button button--primary" type="button" onClick={() => navigate('/jouer/quotidien')}>Jouer aujourd’hui</button>
        </section>
      )}
    </main>
  )
}
